import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PostfachSettings from './PostfachSettings';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface KonfigEintrag {
  schluessel: string;
  wert: string;
  beschreibung: string;
  ist_passwort: boolean;
  gruppe: string;
  aktualisiert_am?: string;
}

interface Props {
  authToken: string;
}

const GRUPPE_LABELS: Record<string, string> = {
  ALLGEMEIN: 'Betrieb (Zeitzone)',
  ANTHROPIC: 'Claude / Anthropic (KI-Agenten)',
  EMAIL: 'E-Mail-Provider',
  SMTP: 'E-Mail-Versand (SMTP)',
  IMAP: 'Postfachüberwachung (IMAP)',
  GRAPH: 'E-Mail über Microsoft 365 (Graph API)',
};

const GRUPPE_HINWEISE: Record<string, string> = {
  ALLGEMEIN: 'Zeitzone des Betriebs als IANA-Name (z. B. Europe/Berlin, Europe/Vienna). Sie bestimmt den Kalendertag der Agenten und Dashboards, die Uhrzeiten der Einsatzplanung, den Outlook-Abgleich und die Zeitstempel in Beleg-Dateinamen. Leer = Europe/Berlin.',
  ANTHROPIC: 'Der API-Key wird für alle KI-Agenten dieses Mandanten verwendet. Leer = Umgebungsvariable (.env) wird genutzt.',
  EMAIL: 'Wert "smtp" (Standard) nutzt die SMTP/IMAP-Zugangsdaten unten, "graph" nutzt die Microsoft-365-Postfächer (Graph API).',
  IMAP: 'Sobald unter "Überwachte Postfächer" Einträge existieren, ersetzt diese Liste das hier konfigurierte Einzelpostfach.',
  GRAPH: 'Erfordert eine Azure-AD-App-Registrierung im Kunden-Tenant mit Application-Permissions Mail.Send + Mail.ReadWrite und erteiltem Admin-Consent. Absender-/Eingangs-Postfach können funktionale Adressen sein (z.B. rechnung@firma.de, info@firma.de) statt eines persönlichen Postfachs. Sobald unter "Überwachte Postfächer" Einträge existieren, ersetzt diese Liste das hier konfigurierte Einzelpostfach.',
};

interface SslStatus {
  zertifikat_vorhanden: boolean;
  schluessel_vorhanden: boolean;
  aktiv: boolean;
  hinweis: string | null;
  zertifikat_hochgeladen_am?: string;
  zertifikat_groesse?: number;
}

interface OutlookStatus {
  graph_konfiguriert: boolean;
  dispo_mailbox_gesetzt: boolean;
  kalender_bereit: boolean;
  fehlende_schluessel: string[];
  benoetigte_berechtigungen: string[];
}

const KonfigurationSettings: React.FC<Props> = ({ authToken }) => {
  const [eintraege, setEintraege] = useState<KonfigEintrag[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [changed, setChanged] = useState<Set<string>>(new Set());
  const [showPasswort, setShowPasswort] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [ladeError, setLadeError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Record<string, { ok: boolean; text: string }>>({});
  const [smtpTest, setSmtpTest] = useState<{ ok: boolean; text: string } | null>(null);
  const [smtpTesting, setSmtpTesting] = useState(false);

  // SSL State
  const [sslStatus, setSslStatus] = useState<SslStatus | null>(null);
  const [sslUploading, setSslUploading] = useState(false);
  const [sslFeedback, setSslFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [keyFile, setKeyFile] = useState<File | null>(null);

  // Outlook-Einsatzplanung Status
  const [outlookStatus, setOutlookStatus] = useState<OutlookStatus | null>(null);
  const [outlookStatusSichtbar, setOutlookStatusSichtbar] = useState(true);
  const [outlookStatusFehler, setOutlookStatusFehler] = useState<string | null>(null);
  const [outlookStatusLoading, setOutlookStatusLoading] = useState(true);

  // Akkordeon-State: welche Karten (Konfig-Gruppen + SSL) sind aufgeklappt
  const [offenKarten, setOffenKarten] = useState<Set<string>>(new Set());
  const [erstOffenGesetzt, setErstOffenGesetzt] = useState(false);

  const headers = { Authorization: `Bearer ${authToken}` };

  const load = async () => {
    setLoading(true);
    setLadeError(null);
    try {
      const res = await axios.get(`${API_URL}/api/konfiguration`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setEintraege(res.data);
      const vals: Record<string, string> = {};
      for (const e of res.data) {
        vals[e.schluessel] = e.wert;
      }
      setValues(vals);
    } catch (e: any) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      setLadeError(
        status === 403
          ? 'Kein Zugriff – nur Admins können die Konfiguration sehen.'
          : detail || `Fehler beim Laden (HTTP ${status || 'unbekannt'})`
      );
    } finally {
      setLoading(false);
    }
  };

  const loadSslStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ssl/status`, { headers });
      setSslStatus(res.data);
    } catch (e) {
      console.error('SSL-Status laden fehlgeschlagen', e);
    }
  };

  const uploadSsl = async () => {
    if (!certFile || !keyFile) return;
    setSslUploading(true);
    setSslFeedback(null);
    try {
      const formData = new FormData();
      formData.append('cert', certFile);
      formData.append('key', keyFile);
      await axios.post(`${API_URL}/api/ssl/upload`, formData, { headers });
      setSslFeedback({ ok: true, text: 'Zertifikat gespeichert. Frontend-Container muss neu gestartet werden.' });
      setCertFile(null);
      setKeyFile(null);
      loadSslStatus();
    } catch (e: any) {
      setSslFeedback({ ok: false, text: e.response?.data?.detail || 'Upload fehlgeschlagen' });
    } finally {
      setSslUploading(false);
    }
  };

  const removeSsl = async () => {
    try {
      await axios.delete(`${API_URL}/api/ssl/remove`, { headers });
      setSslFeedback({ ok: true, text: 'Zertifikate entfernt. Selbstsigniertes Zertifikat wird nach Neustart verwendet.' });
      loadSslStatus();
    } catch (e: any) {
      setSslFeedback({ ok: false, text: e.response?.data?.detail || 'Fehler' });
    }
  };

  const loadOutlookStatus = async () => {
    setOutlookStatusLoading(true);
    setOutlookStatusFehler(null);
    try {
      const res = await axios.get(`${API_URL}/api/einsaetze/outlook-status`, { headers });
      setOutlookStatus(res.data);
      setOutlookStatusSichtbar(true);
    } catch (e: any) {
      if (e.response?.status === 404) {
        // Modul Einsatzplanung deaktiviert – Box ausblenden statt Fehler zeigen
        setOutlookStatusSichtbar(false);
      } else {
        setOutlookStatusFehler(e.response?.data?.detail || 'Outlook-Status konnte nicht geladen werden');
      }
    } finally {
      setOutlookStatusLoading(false);
    }
  };

  useEffect(() => { load(); loadSslStatus(); loadOutlookStatus(); }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Erste Konfig-Gruppe standardmäßig aufgeklappt, sobald die Daten da sind
  useEffect(() => {
    if (erstOffenGesetzt || eintraege.length === 0) return;
    const ersteGruppe = eintraege[0]?.gruppe;
    if (ersteGruppe) setOffenKarten(new Set([ersteGruppe]));
    setErstOffenGesetzt(true);
  }, [eintraege, erstOffenGesetzt]);

  const toggleKarte = (id: string) => {
    setOffenKarten(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleChange = (schluessel: string, val: string) => {
    setValues(prev => ({ ...prev, [schluessel]: val }));
    setChanged(prev => new Set(prev).add(schluessel));
  };

  const saveGruppe = async (gruppe: string) => {
    const gruppenEintraege = eintraege.filter(e => e.gruppe === gruppe);
    setSaving(gruppe);
    const newFeedback: Record<string, { ok: boolean; text: string }> = {};

    for (const e of gruppenEintraege) {
      if (!changed.has(e.schluessel)) continue;
      try {
        await axios.put(
          `${API_URL}/api/konfiguration/${e.schluessel}`,
          { wert: values[e.schluessel] ?? '' },
          { headers }
        );
        newFeedback[e.schluessel] = { ok: true, text: 'Gespeichert' };
      } catch (err: any) {
        newFeedback[e.schluessel] = { ok: false, text: err.response?.data?.detail || 'Fehler' };
      }
    }

    setFeedback(prev => ({ ...prev, ...newFeedback }));
    setChanged(prev => {
      const next = new Set(prev);
      gruppenEintraege.forEach(e => next.delete(e.schluessel));
      return next;
    });
    setSaving(null);

    // Feedback nach 3s ausblenden
    setTimeout(() => {
      setFeedback(prev => {
        const next = { ...prev };
        gruppenEintraege.forEach(e => delete next[e.schluessel]);
        return next;
      });
    }, 3000);
  };

  const testSmtp = async () => {
    setSmtpTesting(true);
    setSmtpTest(null);
    try {
      const res = await axios.get(`${API_URL}/api/email/test-verbindung`, { headers });
      setSmtpTest({ ok: res.data.ok, text: res.data.ok ? 'Verbindung erfolgreich' : (res.data.fehler || 'Verbindung fehlgeschlagen') });
    } catch (e: any) {
      setSmtpTest({ ok: false, text: e.response?.data?.detail || 'Verbindung fehlgeschlagen' });
    } finally {
      setSmtpTesting(false);
    }
  };

  const EMAIL_TEST_GRUPPEN = ['SMTP', 'GRAPH'];

  const togglePasswort = (key: string) => {
    setShowPasswort(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const gruppen = [...new Set(eintraege.map(e => e.gruppe))].filter(Boolean);

  // "Überwachte Postfächer"-Karte zwischen IMAP und GRAPH einsortieren
  // (Fallback: nach der letzten Gruppe, falls IMAP nicht existiert)
  const postfachAnkerGruppe = gruppen.includes('IMAP') ? 'IMAP' : gruppen[gruppen.length - 1];

  const postfachKarte = (
    <PostfachSettings
      authToken={authToken}
      istOffen={offenKarten.has('postfaecher')}
      onToggle={() => toggleKarte('postfaecher')}
    />
  );

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-2)' }}>Lädt Konfiguration...</div>;
  }

  if (ladeError) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ background: 'var(--bone-2)', border: '1px solid var(--bone-2)', borderRadius: 0, padding: '16px 20px' }}>
          <strong style={{ color: 'var(--red)' }}>Fehler beim Laden</strong>
          <p style={{ margin: '8px 0 12px', color: 'var(--red)', fontSize: 14 }}>{ladeError}</p>
          <button onClick={load} style={{ padding: '6px 16px', background: 'var(--red)', color: 'var(--bone)', border: 'none', borderRadius: 0, cursor: 'pointer', fontSize: 13 }}>
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
      <h2 style={{ marginBottom: 4, fontSize: 22 }}>System-Einstellungen</h2>
      <p style={{ color: 'var(--ink-2)', marginBottom: 24, fontSize: 14 }}>
        Konfiguration wird in der Datenbank gespeichert und sofort aktiv –
        kein Serverneustart nötig. Leeres Feld = Umgebungsvariable (.env) wird verwendet.
      </p>

      {gruppen.map(gruppe => {
        const gruppenEintraege = eintraege.filter(e => e.gruppe === gruppe);
        const hatAenderungen = gruppenEintraege.some(e => changed.has(e.schluessel));
        const istOffen = offenKarten.has(gruppe);

        return (
          <React.Fragment key={gruppe}>
          <div style={{
            background: 'var(--bone)',
            border: '1px solid var(--bone-3)',
            borderRadius: 0,
            marginBottom: 24,
            overflow: 'hidden',
          }}>
            {/* Gruppen-Header (klickbar = auf-/zuklappen) */}
            <div
              onClick={() => toggleKarte(gruppe)}
              style={{
                background: 'var(--bone-2)',
                borderBottom: istOffen ? '1px solid var(--bone-3)' : 'none',
                padding: '12px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 4, userSelect: 'none' }}>
                  {istOffen ? '▾' : '▸'}
                </span>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                    {GRUPPE_LABELS[gruppe] || gruppe}
                    {!istOffen && hatAenderungen && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--gold)' }}>
                        · ungespeicherte Änderungen
                      </span>
                    )}
                  </h3>
                  {GRUPPE_HINWEISE[gruppe] && (
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-2)', fontWeight: 400 }}>
                      {GRUPPE_HINWEISE[gruppe]}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {EMAIL_TEST_GRUPPEN.includes(gruppe) && (
                  <button
                    onClick={e => { e.stopPropagation(); testSmtp(); }}
                    disabled={smtpTesting}
                    style={{
                      padding: '5px 14px', fontSize: 13,
                      background: 'var(--green-900)', color: 'var(--bone)',
                      border: 'none', borderRadius: 0, cursor: 'pointer',
                    }}
                  >
                    {smtpTesting ? 'Teste...' : 'Verbindung testen'}
                  </button>
                )}
                <button
                  onClick={e => { e.stopPropagation(); saveGruppe(gruppe); }}
                  disabled={!hatAenderungen || saving === gruppe}
                  style={{
                    padding: '5px 16px', fontSize: 13, fontWeight: 600,
                    background: hatAenderungen ? 'var(--green-500)' : 'var(--bone-3)',
                    color: 'var(--bone)', border: 'none', borderRadius: 0,
                    cursor: hatAenderungen ? 'pointer' : 'not-allowed',
                  }}
                >
                  {saving === gruppe ? 'Speichert...' : 'Speichern'}
                </button>
              </div>
            </div>

            {istOffen && (
              <>
                {/* Verbindungstest-Ergebnis */}
                {EMAIL_TEST_GRUPPEN.includes(gruppe) && smtpTest && (
                  <div style={{
                    padding: '8px 20px',
                    background: smtpTest.ok ? 'var(--bone-2)' : 'var(--bone-2)',
                    borderBottom: '1px solid var(--bone-3)',
                    fontSize: 13,
                    color: smtpTest.ok ? 'var(--green-500)' : 'var(--red)',
                  }}>
                    {smtpTest.ok ? '✓' : '✗'} {smtpTest.text}
                  </div>
                )}

                {/* Felder */}
                <div style={{ padding: '8px 20px' }}>
                  {gruppenEintraege.map(e => {
                    const isChanged = changed.has(e.schluessel);
                    const fb = feedback[e.schluessel];
                    const sichtbar = showPasswort.has(e.schluessel);

                    return (
                      <div key={e.schluessel} style={{
                        display: 'grid',
                        gridTemplateColumns: '220px 1fr',
                        gap: '0 16px',
                        alignItems: 'center',
                        padding: '10px 0',
                        borderBottom: '1px solid var(--bone-2)',
                      }}>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>{e.beschreibung}</div>
                          <div style={{ fontSize: 11, color: 'var(--ink-2)', fontFamily: 'monospace' }}>
                            {e.schluessel}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input
                            type={e.ist_passwort && !sichtbar ? 'password' : 'text'}
                            value={values[e.schluessel] ?? ''}
                            onChange={ev => handleChange(e.schluessel, ev.target.value)}
                            placeholder="Leer = .env wird verwendet"
                            style={{
                              flex: 1,
                              padding: '6px 10px',
                              border: `1px solid ${isChanged ? 'var(--green-900)' : 'var(--bone-3)'}`,
                              borderRadius: 0,
                              fontSize: 13,
                              fontFamily: e.ist_passwort ? 'monospace' : 'inherit',
                              background: isChanged ? 'var(--bone-2)' : 'var(--bone)',
                            }}
                          />
                          {e.ist_passwort && (
                            <button
                              onClick={() => togglePasswort(e.schluessel)}
                              style={{
                                padding: '6px 8px', fontSize: 12,
                                border: '1px solid var(--bone-3)', borderRadius: 0,
                                background: 'var(--bone-2)', cursor: 'pointer',
                              }}
                              title={sichtbar ? 'Verbergen' : 'Anzeigen'}
                            >
                              {sichtbar ? 'Verbergen' : 'Anzeigen'}
                            </button>
                          )}
                          {fb && (
                            <span style={{
                              fontSize: 12, whiteSpace: 'nowrap',
                              color: fb.ok ? 'var(--green-500)' : 'var(--red)',
                            }}>
                              {fb.ok ? '✓' : '✗'} {fb.text}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Outlook-Einsatzplanung Status – nur bei der GRAPH-Gruppe */}
                {gruppe === 'GRAPH' && outlookStatusSichtbar && (
                  <div style={{ padding: '0 20px 16px' }}>
                    {outlookStatusLoading ? (
                      <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>Lade Outlook-Status…</div>
                    ) : outlookStatusFehler ? (
                      <div style={{
                        padding: '10px 14px',
                        background: 'var(--bone-2)',
                        border: '1px solid var(--bone-3)',
                        fontSize: 12,
                        color: 'var(--red)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}>
                        <span>Outlook-Status konnte nicht geladen werden: {outlookStatusFehler}</span>
                        <button
                          onClick={loadOutlookStatus}
                          style={{ marginLeft: 'auto', padding: '3px 10px', border: '1px solid var(--red)', background: 'none', color: 'var(--red)', fontSize: 11, cursor: 'pointer' }}
                        >
                          Erneut versuchen
                        </button>
                      </div>
                    ) : outlookStatus && (
                      <div style={{ border: '1px solid var(--bone-3)', background: 'var(--bone-2)', padding: '14px 16px' }}>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
                          Outlook-Einsatzplanung
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          color: outlookStatus.kalender_bereit ? 'var(--green-500)' : 'var(--gold)',
                          marginBottom: 8,
                        }}>
                          {outlookStatus.kalender_bereit ? '✓ Outlook-Spiegel bereit' : '⚠ Einrichtung unvollständig'}
                        </div>

                        {!outlookStatus.kalender_bereit && outlookStatus.fehlende_schluessel.length > 0 && (
                          <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--ink-2)' }}>
                            <div style={{ marginBottom: 4 }}>Fehlende Schlüssel:</div>
                            <ul style={{ margin: '0 0 6px', paddingLeft: 18 }}>
                              {outlookStatus.fehlende_schluessel.map(k => (
                                <li key={k} style={{ fontFamily: 'monospace' }}>{k}</li>
                              ))}
                            </ul>
                            <div>Diese Schlüssel werden hier in der Gruppe „{GRUPPE_LABELS.GRAPH || 'GRAPH'}“ gepflegt.</div>
                          </div>
                        )}

                        <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                          <div style={{ marginBottom: 4, fontWeight: 500 }}>Benötigte Azure-Berechtigungen:</div>
                          <ul style={{ margin: '0 0 6px', paddingLeft: 18 }}>
                            {outlookStatus.benoetigte_berechtigungen.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                          <div>Details siehe Doku <code style={{ fontFamily: 'monospace' }}>docs/OUTLOOK_EINSATZPLANUNG.md</code>.</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Letztes Aktualisierungsdatum */}
                {gruppenEintraege.some(e => e.aktualisiert_am) && (
                  <div style={{
                    padding: '8px 20px',
                    fontSize: 11, color: 'var(--ink-2)',
                    borderTop: '1px solid var(--bone-2)',
                  }}>
                    Zuletzt geändert:{' '}
                    {new Date(
                      gruppenEintraege
                        .filter(e => e.aktualisiert_am)
                        .sort((a, b) => (b.aktualisiert_am! > a.aktualisiert_am! ? 1 : -1))[0]
                        ?.aktualisiert_am!
                    ).toLocaleString('de-DE')}
                  </div>
                )}
              </>
            )}
          </div>
          {gruppe === postfachAnkerGruppe && postfachKarte}
          </React.Fragment>
        );
      })}

      {/* Falls keine Konfig-Gruppen existieren, Postfach-Karte trotzdem anzeigen */}
      {gruppen.length === 0 && postfachKarte}

      {/* SSL-Zertifikat — eigene Karte am Ende */}
      <div style={{
        background: 'var(--bone)',
        border: '1px solid var(--bone-3)',
        borderRadius: 0,
        marginBottom: 24,
        overflow: 'hidden',
      }}>
        <div
          onClick={() => toggleKarte('ssl')}
          style={{
            background: 'var(--bone-2)',
            borderBottom: offenKarten.has('ssl') ? '1px solid var(--bone-3)' : 'none',
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: 'var(--ink-2)', userSelect: 'none' }}>
              {offenKarten.has('ssl') ? '▾' : '▸'}
            </span>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
              SSL-Zertifikat
            </h3>
          </div>
          {sslStatus?.aktiv && (
            <button
              onClick={e => { e.stopPropagation(); removeSsl(); }}
              style={{
                padding: '5px 14px', fontSize: 13,
                background: 'var(--bone-2)', color: 'var(--gold)',
                border: '1px solid var(--bone-2)', borderRadius: 0, cursor: 'pointer',
              }}
            >
              Zertifikat entfernen
            </button>
          )}
        </div>

        {offenKarten.has('ssl') && (
          <>
            {sslFeedback && (
              <div style={{
                padding: '8px 20px',
                background: sslFeedback.ok ? 'var(--bone-2)' : 'var(--bone-2)',
                borderBottom: '1px solid var(--bone-3)',
                fontSize: 13,
                color: sslFeedback.ok ? 'var(--green-500)' : 'var(--red)',
              }}>
                {sslFeedback.ok ? '✓' : '✗'} {sslFeedback.text}
              </div>
            )}

            <div style={{ padding: '16px 20px' }}>
              {sslStatus && (
                <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--ink-2)' }}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Status:</strong>{' '}
                    <span style={{
                      padding: '2px 10px',
                      borderRadius: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      background: sslStatus.aktiv ? 'var(--bone-2)' : 'var(--bone-2)',
                      color: sslStatus.aktiv ? 'var(--green-500)' : 'var(--gold)',
                    }}>
                      {sslStatus.aktiv ? 'Eigenes Zertifikat aktiv' : 'Selbstsigniert (Standard)'}
                    </span>
                  </div>
                  {sslStatus.zertifikat_hochgeladen_am && (
                    <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                      Hochgeladen: {new Date(sslStatus.zertifikat_hochgeladen_am).toLocaleString('de-DE')}
                    </div>
                  )}
                </div>
              )}

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                marginBottom: 12,
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                    Zertifikat (cert.pem)
                  </label>
                  <input
                    type="file"
                    accept=".pem,.crt,.cer"
                    onChange={e => setCertFile(e.target.files?.[0] || null)}
                    style={{ fontSize: 13 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                    Privater Schluessel (key.pem)
                  </label>
                  <input
                    type="file"
                    accept=".pem,.key"
                    onChange={e => setKeyFile(e.target.files?.[0] || null)}
                    style={{ fontSize: 13 }}
                  />
                </div>
              </div>

              <button
                onClick={uploadSsl}
                disabled={!certFile || !keyFile || sslUploading}
                style={{
                  padding: '8px 20px', fontSize: 13, fontWeight: 600,
                  background: certFile && keyFile ? 'var(--green-500)' : 'var(--bone-3)',
                  color: 'var(--bone)', border: 'none', borderRadius: 0,
                  cursor: certFile && keyFile ? 'pointer' : 'not-allowed',
                }}
              >
                {sslUploading ? 'Wird hochgeladen...' : 'Zertifikat hochladen'}
              </button>

              <p style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 12, marginBottom: 0 }}>
                PEM-Format erforderlich. Nach dem Upload muss der Frontend-Container
                neu gestartet werden (Redeployment oder manuell via Docker).
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KonfigurationSettings;
